import React from 'react';
import { Box, Container, Typography, Paper, Divider } from '@mui/material';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            Privacy Policy
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            color="text.secondary" 
            sx={{ mb: 6 }}
          >
            Last Updated: January 4, 2026
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              backgroundColor: 'background.paper',
              borderRadius: 3,
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Welcome to InnoVate Games. We are committed to protecting your personal information and your right to privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our 
              website or use our mobile applications.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              2. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We may collect information about you in a variety of ways. The information we may collect includes:
            </Typography>
            <Typography variant="body1" component="div" sx={{ lineHeight: 1.8, mb: 2 }}>
              <ul>
                <li><strong>Personal Data:</strong> Name, email address, and contact information you provide when contacting us.</li>
                <li><strong>Device Information:</strong> Information about your device, operating system, and browser type.</li>
                <li><strong>Usage Data:</strong> Information about how you use our games and website.</li>
                <li><strong>Game Data:</strong> Game progress, scores, and achievements within our applications.</li>
              </ul>
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We use the information we collect to:
            </Typography>
            <Typography variant="body1" component="div" sx={{ lineHeight: 1.8, mb: 2 }}>
              <ul>
                <li>Provide, operate, and maintain our games and services</li>
                <li>Improve, personalize, and expand our games</li>
                <li>Understand and analyze how you use our games</li>
                <li>Develop new products, services, features, and functionality</li>
                <li>Communicate with you for customer service and updates</li>
                <li>Send you promotional communications (with your consent)</li>
              </ul>
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              4. Data Security
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We use administrative, technical, and physical security measures to help protect your personal information. 
              While we have taken reasonable steps to secure the personal information you provide to us, please be aware that 
              despite our efforts, no security measures are perfect or impenetrable.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              5. Third-Party Services
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We may share your information with third parties that perform services for us or on our behalf, including:
            </Typography>
            <Typography variant="body1" component="div" sx={{ lineHeight: 1.8, mb: 2 }}>
              <ul>
                <li>Google Play Services for game distribution</li>
                <li>Analytics providers to understand app usage</li>
                <li>Cloud service providers for data storage</li>
              </ul>
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              6. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Our games may be suitable for children. We do not knowingly collect personal information from children under 13 
              without parental consent. If you are a parent or guardian and believe your child has provided us with personal 
              information, please contact us.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              7. Your Privacy Rights
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </Typography>
            <Typography variant="body1" component="div" sx={{ lineHeight: 1.8, mb: 2 }}>
              <ul>
                <li>The right to access your personal data</li>
                <li>The right to correct inaccurate data</li>
                <li>The right to request deletion of your data</li>
                <li>The right to withdraw consent</li>
              </ul>
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              8. Updates to This Policy
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We may update this Privacy Policy from time to time. The updated version will be indicated by an updated 
              "Last Updated" date at the top of this Privacy Policy. We encourage you to review this Privacy Policy 
              periodically to stay informed about how we are protecting your information.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              9. Contact Us
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              If you have questions or comments about this Privacy Policy, please contact us at:
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              <strong>InnoVate Games & Apps</strong><br />
              House no. 311 street 17 Villa comunity DHA Bahawalpur, Pakistan<br />
              Email: info@innovategames.net
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
